import React, { useEffect, useMemo, useRef } from 'react';
import { ApolloProvider, useLazyQuery, useQuery } from '@apollo/client/react';
import { Environments } from '@atlassian/omni-channel';
import { OCPWidget, DisplayMode } from '@atlassian/omni-channel/ocp-widget';
import config from 'appconfig';
import { omniChannelApolloClient } from './graphql/client';
import { getChannelPlatformTokenByClientName, getCustomerConversations } from './graphql/queries';
import {
  transformPayloadForChannelPlatform,
  constructMetadataWithFlowId,
  getAnonymousId,
} from './utils';
import {
  sendCustomerJoinedConversationOmnichannelEvent,
  sendOmnichannelChatConnectionRefreshSuccessEvent,
  sendOmnichannelCommunicationHubErrorEvent,
  sendOmnichannelTokenGenerationSuccessEvent,
  sendCustomerConversationTabClosedOmnichannelEvent,
  sendOmnichannelWidgetLoadedEvent,
} from './analytics';

const CLIENT_DETAILS = {
  clientId: 'sales',
  clientFormKey: 'cart-web-app',
  isAuthenticated: true,
};

const FILTERED_CHANNEL_PLATFORM_CHANNELS = ['CHAT', 'TICKET'];

interface SalesChannelOcpWidgetContentProps {
  email?: string;
  userName?: string;
  isAuthenticated?: boolean;
  visible?: boolean;
}

export const SalesChannelOcpWidgetContent = ({
  email,
  isAuthenticated,
  visible = true,
}: SalesChannelOcpWidgetContentProps) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const formValues = useMemo(
    () => ({
      details: 'Support inquiry from Shopping Cart',
      contactEmail: email,
      isAuthenticated,
      timezone,
    }),
    [email, isAuthenticated, timezone],
  );

  const hasTrackedWidgetDisplayRef = useRef(false);

  useEffect(() => {
    const handleBeforeUnload = () => {
      sendCustomerConversationTabClosedOmnichannelEvent(CLIENT_DETAILS);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      sendCustomerConversationTabClosedOmnichannelEvent(CLIENT_DETAILS);
    };
  }, []);

  const { error: channelPlatformTokenError, data: channelPlatformToken } = useQuery(
    getChannelPlatformTokenByClientName,
    {
      variables: {
        clientName: 'sales',
      },
    },
  );

  const [fetchCustomerConversations, { data: customerConversationsResponse }] = useLazyQuery(
    getCustomerConversations,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  const isRefreshed =
    !!customerConversationsResponse &&
    customerConversationsResponse.channelPlatform_getCustomerConversations?.conversations?.length >
      0;

  useEffect(() => {
    if (channelPlatformToken?.supportInquiry_channelsIdentityHashByClientName) {
      const anonymousId = getAnonymousId();
      fetchCustomerConversations({
        variables: {
          anonymousId,
        },
        context: {
          headers: {
            'X-Client-Hash': channelPlatformToken?.supportInquiry_channelsIdentityHashByClientName,
          },
        },
      });
    }
  }, [
    channelPlatformToken?.supportInquiry_channelsIdentityHashByClientName,
    fetchCustomerConversations,
  ]);

  // OCPWidget renders its own launcher button, so the widget is "displayed"
  // as soon as the identity-hash token is available (no user click required).
  const isWidgetDisplayed =
    visible && !!channelPlatformToken?.supportInquiry_channelsIdentityHashByClientName;
  useEffect(() => {
    if (isWidgetDisplayed && !hasTrackedWidgetDisplayRef.current) {
      hasTrackedWidgetDisplayRef.current = true;
      sendOmnichannelWidgetLoadedEvent({
        // eslint-disable-next-line camelcase
        invocation_platform: 'omnichannel-chat',
        ...CLIENT_DETAILS,
      });
    }
  }, [isWidgetDisplayed]);

  useEffect(() => {
    const conversations =
      customerConversationsResponse?.channelPlatform_getCustomerConversations?.conversations ?? [];
    if (conversations.length > 0) {
      sendCustomerJoinedConversationOmnichannelEvent({
        conversationId: conversations[0],
        ...CLIENT_DETAILS,
      });
    }
  }, [customerConversationsResponse]);

  useEffect(() => {
    if (channelPlatformTokenError) {
      sendOmnichannelCommunicationHubErrorEvent({
        typeOfError: 'channelPlatformTokenError',
        errorMessage: channelPlatformTokenError.message,
        ...CLIENT_DETAILS,
      });
    }
  }, [channelPlatformTokenError]);

  useEffect(() => {
    if (channelPlatformToken?.supportInquiry_channelsIdentityHashByClientName) {
      sendOmnichannelTokenGenerationSuccessEvent({
        supportedChannels: FILTERED_CHANNEL_PLATFORM_CHANNELS,
        ...CLIENT_DETAILS,
      });
    }
  }, [channelPlatformToken?.supportInquiry_channelsIdentityHashByClientName]);

  useEffect(() => {
    const conversations =
      customerConversationsResponse?.channelPlatform_getCustomerConversations?.conversations ?? [];
    if (conversations.length > 0) {
      sendOmnichannelChatConnectionRefreshSuccessEvent({
        conversations: conversations.length,
        allConversationIds: conversations.join(',') || '',
        conversationId: conversations[0] || '',
        ...CLIENT_DETAILS,
      });
    }
  }, [customerConversationsResponse]);

  // When not visible, render nothing — the component stays mounted (preserving
  // cached token and conversation data) but produces no UI.
  if (!visible) {
    return null;
  }

  // Wait for the identity hash before mounting OCPWidget (it is required).
  if (!channelPlatformToken?.supportInquiry_channelsIdentityHashByClientName) {
    return null;
  }

  return (
    <OCPWidget
      env={config.omniChannelEnvironment as Environments}
      graphqlBaseUrl={
        process.env.OMNICHANNEL_GRAPHQL_ENDPOINT || config.omniChannelGraphQLUrl || ''
      }
      clientDetails={CLIENT_DETAILS}
      clientIdentityHash={channelPlatformToken.supportInquiry_channelsIdentityHashByClientName}
      chatCapabilityEnabled
      customerImageURL={undefined}
      formPayload={transformPayloadForChannelPlatform(formValues)}
      metadata={constructMetadataWithFlowId(formValues, config.omniChannelFlowId)}
      supportedChannels={FILTERED_CHANNEL_PLATFORM_CHANNELS}
      isRefreshed={isRefreshed}
      customerConversations={customerConversationsResponse}
      accountId={undefined}
      displayMode={DisplayMode.FLOATING_SINGLE_SCREEN}
      defaultFloatingWidgetOpen={false}
      enableCustomerTextAreaInputBox={false}
      enableEmailInputBox={false}
      isCustomBotEnabled={true}
      anonymousId={getAnonymousId() || undefined}
      enableFloatingWidgetOpenSound={true}
    />
  );
};

interface SalesChannelOcpWidgetProps {
  email?: string;
  userName?: string;
  isAuthenticated?: boolean;
  visible?: boolean;
}

export const SalesChannelOcpWidget = ({
  email,
  userName,
  isAuthenticated,
  visible,
}: SalesChannelOcpWidgetProps) => {
  return (
    <ApolloProvider client={omniChannelApolloClient}>
      <SalesChannelOcpWidgetContent
        email={email}
        userName={userName}
        isAuthenticated={isAuthenticated}
        visible={visible}
      />
    </ApolloProvider>
  );
};

export default SalesChannelOcpWidget;
